import type Map from 'ol/Map';
import { coordinateFromMap, formatCoordinate, type GeoCoordinate } from '../domain/coordinates';
import { icon, type IconName } from './icons';

export type MapContextAction = 'copy' | 'mark' | 'center';

const ITEMS: readonly { readonly action: MapContextAction; readonly label: string; readonly icon: IconName }[] = [
  { action: 'copy', label: 'Copiar coordenada', icon: 'copy' },
  { action: 'mark', label: 'Marcar ubicación', icon: 'point' },
  { action: 'center', label: 'Centrar aquí', icon: 'target' },
];
const VIEWPORT_MARGIN = 8;

/**
 * Compact right-click menu over the map. It only opens when `canOpen()` allows it (navigation/selection tools);
 * while drawing, measuring or cutting, the browser event is left untouched so the active tool keeps priority.
 */
export class MapContextMenu {
  private element: HTMLElement | null = null;
  private coordinate: GeoCoordinate | null = null;

  constructor(private readonly map: Map, private readonly canOpen: () => boolean, private readonly onAction: (action: MapContextAction, coordinate: GeoCoordinate) => void) {
    map.getViewport().addEventListener('contextmenu', (event) => this.onContextMenu(event));
    // Capture phase: Escape closes the menu without reaching the global "Esc clears selection" shortcut.
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.element) return;
      event.preventDefault(); event.stopPropagation(); this.close();
    }, true);
    window.addEventListener('pointerdown', (event) => { if (this.element && !this.element.contains(event.target as Node)) this.close(); }, true);
    window.addEventListener('resize', () => this.close());
    window.addEventListener('blur', () => this.close());
    map.on('movestart', () => this.close());
  }

  get isOpen(): boolean { return Boolean(this.element); }

  /** Opens at viewport coordinates for a map coordinate (also used by tests). */
  open(clientX: number, clientY: number, coordinate: GeoCoordinate): void {
    this.close();
    this.coordinate = coordinate;
    const menu = document.createElement('div');
    menu.className = 'context-menu map-context-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'Opciones del punto');
    menu.innerHTML = `<div class="map-context-coordinate">${formatCoordinate(coordinate)}</div>${ITEMS.map((item) => `<button type="button" role="menuitem" class="menu-item" data-map-context="${item.action}">${icon(item.icon)}<span>${item.label}</span></button>`).join('')}`;
    menu.addEventListener('click', (event) => {
      const button = (event.target as Element).closest<HTMLElement>('[data-map-context]');
      if (!button || !this.coordinate) return;
      const target = this.coordinate; this.close();
      this.onAction(button.dataset.mapContext as MapContextAction, target);
    });
    menu.addEventListener('keydown', (event) => this.onKey(event));
    document.body.append(menu);
    this.element = menu;
    // Keep the whole menu inside the viewport.
    const { width, height } = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(clientX, window.innerWidth - width - VIEWPORT_MARGIN))}px`;
    menu.style.top = `${Math.max(VIEWPORT_MARGIN, Math.min(clientY, window.innerHeight - height - VIEWPORT_MARGIN))}px`;
    menu.querySelector<HTMLElement>('[data-map-context]')?.focus({ preventScroll: true });
  }

  close(): void {
    this.element?.remove();
    this.element = null;
    this.coordinate = null;
  }

  private onContextMenu(event: MouseEvent): void {
    if (!this.canOpen()) { this.close(); return; }
    event.preventDefault();
    this.open(event.clientX, event.clientY, coordinateFromMap(this.map.getEventCoordinate(event)));
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [...(this.element?.querySelectorAll<HTMLElement>('[data-map-context]') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  }
}
